/**
 * Google Analytics 4 (gtag.js) — production only.
 *
 * Loads Google's `gtag.js` and fires the initial `page_view` (via `config`),
 * but ONLY when we're on the production host — never staging, a Vercel preview,
 * or localhost — so our own dev/preview visits never pollute GA. This mirrors
 * the Meta Pixel doctrine in `meta-pixel.ts`; we reuse its `isProductionHost`
 * gate rather than re-deriving it.
 *
 * The measurement id is public (not a secret), so it defaults to the baked-in
 * `G-Q6HHZ3C4S0` and GA works on production with no env setup. A build may
 * override it with `VITE_GA_MEASUREMENT_ID` (e.g. to point a preview at a
 * separate property) without a code change.
 *
 * Like the Meta Pixel, this lives in the portal shell only; games run in
 * sandboxed iframes (separate documents) so GA never enters a game — preserving
 * the `/privacy` "no tracking ever runs inside the games" promise.
 */
import { isProductionHost } from "./meta-pixel.js";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Public GA4 measurement id. Overridable per-build; defaults to the property
 * created for the production portal. */
const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? "G-Q6HHZ3C4S0";

let enabled = false;

/**
 * Boot Google Analytics + fire the initial page_view. No-op unless we're on the
 * production host and a measurement id is present. Idempotent.
 */
export function initGoogleAnalytics(): void {
  if (typeof window === "undefined") return; // SSR / prerender
  if (enabled) return;
  if (!GA_ID) return;
  if (!isProductionHost(window.location.hostname)) return;

  // Google's loader stub, expressed in TS. `dataLayer.push(args)` mirrors the
  // official `function gtag(){dataLayer.push(arguments)}` — gtag.js reads each
  // entry positionally, so a rest array is equivalent to `arguments`.
  window.dataLayer = window.dataLayer || [];
  const gtag = (...args: unknown[]): void => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  gtag("config", GA_ID);
  enabled = true;
}
